import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import type { Client, TalentInvoice, Talent } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { getTimeZoneAbbreviation } from "@/lib/utils";

// Talent users see their own bookings (one row per invoice/show, with
// eventTime backfilled from the linked client by `getInvoicesByTalent`
// — see Task #194). Non-talent users (admin/sales) keep the legacy
// "all clients in workspace" view since /schedule is shared.
type ScheduleRow = {
  id: string;
  dateOfEvent: Date | string | null;
  eventTime: string | null;
  accountName: string | null;
  address: string | null;
  rate: string | null;
};

export default function SchedulePage() {
  const { user } = useAuth();

  // Resolve the talent lookup BEFORE picking a data source so a talent
  // user never sees an N=workspace-clients flash while their /by-user
  // request is still in flight (and so we don't kick off an unneeded
  // /api/clients fetch). `isFetched` flips to true on either success
  // OR a 404 (`retry: false`).
  const {
    data: talent,
    isFetched: talentResolved,
  } = useQuery<Talent>({
    queryKey: ["/api/talent/by-user", user?.id],
    enabled: !!user?.id,
    retry: false,
  });

  const isTalent = !!talent;

  const { data: clients = [], isLoading: clientsLoading } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    enabled: talentResolved && !isTalent,
  });

  const { data: talentInvoices = [], isLoading: invoicesLoading } = useQuery<
    TalentInvoice[]
  >({
    queryKey: ["/api/talent", talent?.id, "invoices"],
    enabled: !!talent?.id,
  });

  const isLoading = !talentResolved || (isTalent ? invoicesLoading : clientsLoading);

  const rows: ScheduleRow[] = useMemo(() => {
    if (isTalent) {
      return talentInvoices.map((inv) => ({
        id: inv.id,
        dateOfEvent: inv.dateOfEvent,
        eventTime: inv.eventTime,
        accountName: inv.accountName,
        address: inv.accountAddress,
        rate: inv.eventRate,
      }));
    }
    return clients.map((c) => ({
      id: c.id,
      dateOfEvent: c.dateOfEvent,
      eventTime: c.eventTime,
      accountName: c.accountName,
      address: c.address,
      rate: c.rate,
    }));
  }, [isTalent, talentInvoices, clients]);

  const scheduleByMonth = useMemo(() => {
    const grouped: Record<string, ScheduleRow[]> = {};

    rows
      .filter((r) => r.dateOfEvent)
      .sort((a, b) => {
        const dateA = a.dateOfEvent ? new Date(a.dateOfEvent).getTime() : 0;
        const dateB = b.dateOfEvent ? new Date(b.dateOfEvent).getTime() : 0;
        return dateA - dateB;
      })
      .forEach((row) => {
        if (!row.dateOfEvent) return;
        // dateOfEvent is a calendar-day YYYY-MM-DD column — parse without tz-shift
        // to avoid UTC-midnight → previous-day drift.
        const [y, mo] = (row.dateOfEvent as string).split("-").map(Number);
        const monthKey = new Date(y, mo - 1, 1).toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(row);
      });

    return grouped;
  }, [rows]);

  const formatDate = (date: Date | string) => {
    // dateOfEvent is a calendar-day YYYY-MM-DD column — parse without tz-shift.
    const s = typeof date === "string" ? date : date.toISOString().slice(0, 10);
    const [y, mo, d] = s.split("-").map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Convert a show's event date into the `YYYY-MM-DD` string the run
  // sheet page expects in `?day=`. dateOfEvent is a calendar-day date column —
  // return the YYYY-MM-DD string directly to avoid tz-induced day drift.
  const dayParam = (date: Date | string) => {
    if (typeof date === "string") return date.slice(0, 10);
    const [y, mo, d] = date.toISOString().slice(0, 10).split("-");
    return `${y}-${mo}-${d}`;
  };

  const formatTime = (time: string | null) => {
    if (!time) return "";
    try {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  const isPast = (date: Date | string) => {
    return new Date(date) < new Date();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const months = Object.keys(scheduleByMonth);

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">Schedule</h1>
        <p className="text-sm text-muted-foreground">All scheduled shows by month</p>
      </div>

      {months.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground bg-card border border-card-border rounded-lg">
          <p className="text-lg font-medium">No scheduled shows</p>
          <p className="text-sm">Shows will appear here once dates are set.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {months.map((month) => (
            <div key={month}>
              <h2 className="text-lg font-medium mb-4 sticky top-0 bg-background py-2 z-10">
                {month}
              </h2>
              <div className="space-y-2">
                {scheduleByMonth[month].map((show) => (
                  <Card
                    key={show.id}
                    className={`p-4 flex items-center gap-4 ${
                      isPast(show.dateOfEvent!) ? "opacity-50" : ""
                    }`}
                    data-testid={`card-schedule-show-${show.id}`}
                  >
                    <div className="w-20 shrink-0 text-center">
                      <p className="text-2xl font-bold">
                        {Number((show.dateOfEvent as string).slice(8, 10))}
                      </p>
                      <p className="text-xs text-muted-foreground uppercase">
                        {(() => {
                          const s = (show.dateOfEvent as string).slice(0, 10);
                          const [y, mo, d] = s.split("-").map(Number);
                          return new Date(y, mo - 1, d).toLocaleDateString("en-US", { weekday: "short" });
                        })()}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{show.accountName || "Unnamed Venue"}</p>
                      <Link
                        href={`/runsheet?day=${dayParam(show.dateOfEvent!)}`}
                        className="text-sm text-muted-foreground truncate block hover:text-foreground hover:underline"
                        data-testid={`text-schedule-time-${show.id}`}
                      >
                        {show.eventTime ? `${formatTime(show.eventTime)} ${getTimeZoneAbbreviation()}` : "Time TBD"}
                        {show.address && ` • ${show.address}`}
                      </Link>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      {show.rate && (
                        <Badge variant="outline">${parseFloat(show.rate).toFixed(0)}</Badge>
                      )}
                      {isPast(show.dateOfEvent!) && (
                        <Badge variant="secondary">Past</Badge>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
